import { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { IN_APP_FOOTER_LINKS } from './publicFooterLinks';

const PADDLE_SUPPORT_URL = 'https://www.paddle.com/help/manage/your-customers/buyers-refunds';

function PublicPageShell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-white dark:bg-neutral-950 text-neutral-900 dark:text-white flex flex-col">
      <header className="border-b border-neutral-200 dark:border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="text-base sm:text-lg font-bold flex items-center">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="mr-1.5 sm:mr-2">
              <path d="M7 8L3 12L7 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M17 8L21 12L17 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M10 20L14 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>Kolega</span>
            <span className="font-light">Studio</span>
          </Link>
          <Link
            to="/"
            className="bg-brand-primary text-black text-sm font-medium px-4 py-2 rounded-lg hover:bg-brand-primaryDark transition-colors"
          >
            Sign in
          </Link>
        </div>
      </header>
      <div className="flex-1">{children}</div>
      <footer className="border-t border-neutral-200 dark:border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-neutral-600 dark:text-neutral-400">
          {IN_APP_FOOTER_LINKS.map((link) => (
            <Link key={link.href} to={link.href} className="hover:text-neutral-900 dark:hover:text-white transition-colors">
              {link.label}
            </Link>
          ))}
        </div>
        <div className="pb-6 text-center text-xs text-neutral-500 dark:text-neutral-500">
          &copy; {new Date().getFullYear()} Kolega Studio. All rights reserved.
        </div>
      </footer>
    </div>
  );
}

const LINK_CLASSES = 'text-brand-primaryDark dark:text-brand-primary hover:underline';

export default function ContactPage() {
  return (
    <PublicPageShell>
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 text-neutral-700 dark:text-neutral-300">
        <h1 className="text-3xl font-bold text-neutral-900 dark:text-white mb-2">Contact</h1>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-8">We&apos;re happy to help with any question.</p>

        <div className="space-y-6 text-sm">
          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">Get in touch</h3>
            <p className="mb-3">
              For product questions, support, or account help, email us at{' '}
              <a href="mailto:support@kolega.ai" className={LINK_CLASSES}>support@kolega.ai</a>.
              For privacy requests and legal matters, email{' '}
              <a href="mailto:contact@kolega.ai" className={LINK_CLASSES}>contact@kolega.ai</a>.
              We aim to respond within one business day.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">Company details</h3>
            <div className="bg-neutral-100 dark:bg-neutral-900 p-4 rounded-lg">
              <p className="font-medium text-neutral-900 dark:text-white">KLG Tech Innovations Limited</p>
              <p>Val Verclut, La Route des Cotils</p>
              <p>Grouville, JE3 9AP, Jersey</p>
              <p>
                Email: <a href="mailto:contact@kolega.ai" className={LINK_CLASSES}>contact@kolega.ai</a>
              </p>
            </div>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">Billing &amp; payments</h3>
            <p className="mb-3">
              Payments are processed by Paddle, who acts as our Merchant of Record. For invoices, receipts, or
              payment-method questions, contact us at{' '}
              <a href="mailto:support@kolega.ai" className={LINK_CLASSES}>support@kolega.ai</a>, or use
              the{' '}
              <a href={PADDLE_SUPPORT_URL} target="_blank" rel="noopener noreferrer" className={LINK_CLASSES}>
                Paddle Order Support Portal
              </a>
              . See our{' '}
              <Link to="/refund-policy" className={LINK_CLASSES}>Refund Policy</Link> for cancellations and refunds.
            </p>
          </section>
        </div>
      </main>
    </PublicPageShell>
  );
}
